import React, { useState, useEffect, useRef } from 'react';
import { FormInput as Input, ReCarousel } from '@core/frontend/ui';
import { Button, Col, FormGroup, Input as InputUncontrolled, Label, Row } from 'reactstrap';
import {
    SMSVotingProcessStepOneDesktop,
    SMSVotingProcessStepOneMobile,
    SMSVotingProcessStepThree,
    SMSVotingProcessStepThreeDesktop,
    SMSVotingProcessStepTwoMobile,
    WafCaptcha,
} from '@core/frontend/components';
import Cookies from 'js-cookie';
import moment from 'moment';
import { Helmet } from 'react-helmet';
import { sendVirtualPageView } from '../../shared/index.js';
import { httpClient } from '../../http/index.js';
import { Link } from 'react-router-dom';
import { usePhases } from '../../stores/index.js';

/**
 * The three illustration steps of the voting process. Desktop reuses the mobile
 * step two, so the two sets are not permutations of the same list.
 *
 * They depict the campaign's own UI, which is why a product may override them.
 */
const DEFAULT_VOTING_PROCESS_STEPS = {
    mobile: [SMSVotingProcessStepOneMobile, SMSVotingProcessStepTwoMobile, SMSVotingProcessStepThree],
    desktop: [SMSVotingProcessStepOneDesktop, SMSVotingProcessStepTwoMobile, SMSVotingProcessStepThreeDesktop],
};

const SMSRequest = ({
    // Prefix of the campaign's own cookies (`<prefix>_code-requested`, `<prefix>_redirect-category`).
    cookiePrefix = 'bigfoot',
    // Where the success button sends the user once the code has been requested.
    projectListPath = '/projekte/',
    categoryQueryParam = 'category',
    votingProcessSteps = DEFAULT_VOTING_PROCESS_STEPS,
}) => {
    const votingPhases = usePhases();
    const initialWindowSize = window.innerWidth || document.documentElement.clientWidth || document.body.clientWidth;
    const initialRenderCarousel = initialWindowSize < 575;

    const [userInput, setUserInput] = useState('');
    const [pn, setPn] = useState('');
    const [touched, setTouched] = useState(false);
    const [formIsValid, setFormIsValid] = useState(false);
    const [desiredSlide, setDesiredSlide] = useState(0);
    const [renderCarousel, setRenderCarousel] = useState(initialRenderCarousel);
    const [submitSuccess, setSubmitSuccess] = useState(false);
    const [captchaContainerVisibility, setCaptchaContainerVisibility] = useState(false);

    const returnLinkRef = useRef(null);
    const timer = useRef(null);
    const clickChildRef = useRef(null);
    const parsePhoneNumberRef = useRef(null);
    const importPromiseRef = useRef(null);

    const ImageSetMobile = votingProcessSteps.mobile.map((Step, index) => <Step key={index} />);
    const ImageSetDesktop = votingProcessSteps.desktop.map((Step, index) => <Step key={index} />);

    const onResizeHandler = () => {
        let currentWindowSize = window.innerWidth || document.documentElement.clientWidth || document.body.clientWidth;

        let shouldRenderCarousel = currentWindowSize < 575;
        clearTimeout(timer.current);
        timer.current = setTimeout(() => {
            setRenderCarousel(shouldRenderCarousel);
        }, 500);
    };

    useEffect(() => {
        window.addEventListener('resize', onResizeHandler);
        sendVirtualPageView('Jetzt Abstimm-Code anfordern');
        importPromiseRef.current = import('awesome-phonenumber').then((mod) => {
            parsePhoneNumberRef.current = mod.parsePhoneNumber;
        });

        return () => {
            window.removeEventListener('resize', onResizeHandler);
            clearTimeout(timer.current);
        };
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const navigationHandler = (slide) => {
        if (clickChildRef.current) {
            clickChildRef.current(slide);
        }
    };

    const onChangeHandler = async (event) => {
        let currentUserInput = event.target.value;
        if (!parsePhoneNumberRef.current) {
            await importPromiseRef.current;
        }
        let pnum = parsePhoneNumberRef.current(currentUserInput, { regionCode: 'DE' });
        let formattedOutput = pnum.number?.national;
        let isValid = pnum.typeIsMobile && pnum.valid;

        setUserInput(currentUserInput);
        setPn(formattedOutput);
        setTouched(true);
        setFormIsValid(isValid);

        if (renderCarousel) {
            if (isValid) {
                setDesiredSlide(1);
                navigationHandler(1);
            } else {
                setDesiredSlide(0);
                navigationHandler(0);
            }
        } else {
            if (isValid) {
                setDesiredSlide(1);
            } else {
                setDesiredSlide(0);
            }
        }
    };

    const onVoteHandler = () => {
        requestSMS();
    };

    const requestSMS = () => {
        let request = httpClient.post(
            '/sms',
            {
                recipient: pn,
            },
            { wafProtected: true }
        );

        request
            .then((response) => {
                if (response.status === 201) {
                    redirectUser();
                } else {
                    handleUnsuccessfulSMSRequest(response);
                }
            })
            .catch((err) => {
                if (err.response && err.response.status === 405) {
                    setCaptchaContainerVisibility(true);
                } else {
                    handleUnsuccessfulSMSRequest(err);
                }
            });
    };

    const onCaptchaVerified = () => {
        setCaptchaContainerVisibility(false);
        onVoteHandler();
    };

    const redirectUser = () => {
        setDesiredSlide(2);
        setSubmitSuccess(true);

        if (renderCarousel) {
            navigationHandler(2);
        }

        setTimeout(() => {
            if (returnLinkRef.current) {
                returnLinkRef.current.focus();
            }
        }, 0);

        if (!Cookies.get(`${cookiePrefix}_code-requested`)) {
            Cookies.set(`${cookiePrefix}_code-requested`, true, { expires: 7 });
        }
    };

    const handleUnsuccessfulSMSRequest = (responseOrError) => {
        redirectUser();
    };

    let SMSVotingProcess;
    let form;

    const DynamicForm = ({ children }) => {
        return (
            <React.Fragment>
                <FormGroup className="mb-2">
                    <Label for="voteEnded">Ihre Mobilnummer</Label>
                    <InputUncontrolled id="voteEnded" disabled className="mb-2" placeholder="0123 4567890" />
                </FormGroup>
                <Row>
                    <Col>{children}</Col>
                </Row>
            </React.Fragment>
        );
    };

    if (renderCarousel) {
        SMSVotingProcess = (
            <ReCarousel
                axis={'x'}
                className="re-carousel"
                setClick={(click) => (clickChildRef.current = click)}
                desiredSlide={desiredSlide}
                onStartFrameIndex={desiredSlide}
            >
                {ImageSetMobile.map((image, index) => (
                    <div
                        style={
                            index === desiredSlide
                                ? { transition: '1s' }
                                : {
                                      filter: 'grayscale(1)',
                                      transition: '1s',
                                  }
                        }
                        key={index}
                    >
                        {image}
                    </div>
                ))}
            </ReCarousel>
        );
    } else {
        SMSVotingProcess = ImageSetDesktop.map((image, index) => {
            return (
                <Col
                    style={
                        index === desiredSlide
                            ? { transition: '100ms' }
                            : {
                                  filter: 'grayscale(1)',
                                  transition: '100ms',
                              }
                    }
                    key={index}
                >
                    {image}
                </Col>
            );
        });
    }

    const MultiButton = () => {
        if (!submitSuccess) {
            const currentButtonText = formIsValid ? 'Jetzt Code anfordern' : 'Bitte gültige Mobilnummer angeben';
            return (
                <Button
                    block
                    className="position-relative w-100 anl_sms--request"
                    disabled={!formIsValid}
                    color="primary"
                    onClick={onVoteHandler}
                >
                    {currentButtonText}
                </Button>
            );
        } else {
            return (
                <Link
                    className="text-decoration-none"
                    to={
                        Cookies.get(`${cookiePrefix}_redirect-category`)
                            ? `${projectListPath}?${categoryQueryParam}=${Cookies.get(
                                  `${cookiePrefix}_redirect-category`
                              )}`
                            : projectListPath
                    }
                    title="Abstimm-Code erfolgreich angefordert. Jetzt zur Projektliste navigieren und für das Wunschprojekt abstimmen."
                    ref={returnLinkRef}
                >
                    <Button
                        block
                        color="success"
                        className="position-relative w-100 anl_sms--skip-redirect"
                        aria-hidden={true}
                    >
                        Jetzt für das Wunschprojekt abstimmen
                    </Button>
                </Link>
            );
        }
    };

    const PHONENUMBERCONFIG = {
        elementType: 'input',
        elementConfig: {
            type: 'text',
            inputMode: 'numeric',
            pattern: '[0-9 ]+',
            placeholder: '0123 4567890',
            label: 'Ihre Mobilnummer',
            columns: 12,
            disabled: submitSuccess,
        },
        validation: {
            required: { value: true, errorMessage: 'Bitte geben Sie eine gültige Mobilnummer an' },
            // Deutsche Mobilfunknummern haben zwoelf Ziffern (z. B. 0151 12345678);
            // elf blockierten die gueltigen Nummern der 0151er-Bloecke.
            maxLength: { value: 12, errorMessage: 'Bitte geben Sie eine gültige Mobilnummer an' },
            minLength: { value: 10, errorMessage: 'Bitte geben Sie eine gültige Mobilnummer an' },
        },
        errorMessage: 'Bitte geben Sie eine gültige Mobilnummer an',
        ifAuth: false,
    };

    const { appStatus, voteStartFinale } = votingPhases;

    if (appStatus === 'FINALE') {
        form = (
            <React.Fragment>
                <FormGroup row className="mb-0">
                    <Input
                        autoFocus={true}
                        name="mobilePhoneNumber"
                        elementType={PHONENUMBERCONFIG.elementType}
                        elementConfig={PHONENUMBERCONFIG.elementConfig}
                        value={formIsValid ? pn : userInput}
                        changed={(event) => onChangeHandler(event)}
                        shouldValidate={PHONENUMBERCONFIG.validation}
                        touched={touched}
                        errorMessage={PHONENUMBERCONFIG.errorMessage}
                        invalid={!formIsValid}
                        formText={PHONENUMBERCONFIG.formText}
                        wrapperCSSClass="mb-2"
                    />
                </FormGroup>
                <WafCaptcha show={captchaContainerVisibility} onVerified={onCaptchaVerified} />
                {!captchaContainerVisibility && (
                    <Row>
                        <Col>
                            <MultiButton />
                        </Col>
                    </Row>
                )}
            </React.Fragment>
        );
    } else if (appStatus === 'CLOSED') {
        form = (
            <DynamicForm>
                <Button block disabled color="primary" style={{ cursor: 'not-allowed' }}>
                    Der Wettbewerb ist beendet
                </Button>
            </DynamicForm>
        );
    } else {
        // SIGNUP or QUALIFICATION
        form = (
            <DynamicForm>
                <Button block disabled color="primary" style={{ cursor: 'not-allowed' }}>
                    Die Abstimmung startet am {moment(voteStartFinale).format('DD.MM.')}
                </Button>
            </DynamicForm>
        );
    }

    return (
        <React.Fragment>
            <Helmet>
                <title>{`Jetzt Abstimm-Code anfordern | ${process.env.REACT_APP_PROJECT_TITLE}`}</title>
            </Helmet>
            <Row className="mt-3">
                <Col>
                    <h2>Eine Stimme abgeben</h2>
                    <p>Ihrem Favoriten eine Stimme geben!</p>
                </Col>
            </Row>
            <Row>{SMSVotingProcess}</Row>
            <Row className="my-1">
                <Col className="col-12 col-lg-4 col-md-5">{form}</Col>
                <Col className="col-12 col-lg-8 col-md-7 mt-3">
                    <h2>So stimmen Sie ab:</h2>
                    <ul>
                        <li>Geben Sie Ihre Handynummer ein.</li>
                        <li>Per SMS erhalten Sie einen Stimmcode.</li>
                        <li>Der Code muss binnen 48 Stunden eingelöst werden.</li>
                        <li>Lösen Sie den Code direkt auf dem Profil Ihres Favoriten ein, um Ihre Stimme zu werten.</li>
                        <li>Es kann nur ein Code pro Handynummer angefordert werden.</li>
                        <li>Nur deutsche Handynummern können teilnehmen.</li>
                        <li>Der SMS-Empfang ist kostenlos, es entstehen durch die Abstimmung keine Kosten.</li>
                        <li>
                            Die Handynummern dienen ausschließlich der Stimmverifizierung, sie werden{' '}
                            <strong>nicht weitergegeben oder für Werbezwecke verwendet.</strong>
                        </li>
                    </ul>

                    <h2>Ich habe keine SMS mit Abstimmcode erhalten!</h2>
                    <ul>
                        <li>Haben Sie sich eventuell vertippt? Probieren Sie noch einmal, einen Code anzufordern.</li>
                        <li>
                            Die Zustellung der SMS kann manchmal etwas dauern, beispielsweise wenn der Handyempfang
                            schlecht ist. Warten Sie ein wenig ab, meistens erfolgt die Zustellung der SMS binnen 24
                            Stunden. Auf die Zustellung durch die Mobilfunkprovider können wir leider keinen Einfluss
                            nehmen.
                        </li>
                        <li>
                            Um Ihre Daten zu schützen, können wir keine einzelnen Handynummern zurückverfolgen und
                            bestimmten Codes zuordnen. Eine erneute Versendung des Codes ist daher nicht möglich.
                        </li>
                    </ul>

                    <h2>Was geschieht mit den eingegebenen Handynummern?</h2>
                    <ul>
                        <li>Die Angabe der Handynummer dient ausschließlich der Verifizierung der Stimme.</li>
                        <li>
                            Handynummern, die für die Abstimmung verwendet wurden, werden zu keinem Zeitpunkt für eine
                            weitere Nutzung außer für den Versand des Abstimmcodes per SMS verwendet. Eine Weitergabe
                            der Daten oder werbliche Nutzung erfolgt nicht.
                        </li>
                        <li>
                            Die Nummern werden mehrfach verschlüsselt gespeichert und 45 Tage nach Beendigung des
                            Wettbewerbs vollumfänglich gelöscht.
                        </li>
                    </ul>

                    <h2>Wenn ich auf den Button klicke, passiert nichts!</h2>
                    <ul>
                        <li>
                            Möglicherweise ist dies auf eine veraltete Browserversion zurückzuführen. Bitte achten Sie
                            darauf immer die aktuellen Updates Ihres Browsers zu installieren. Insbesondere der Internet
                            Explorer wird von uns nicht mehr unterstützt, da dieser nicht mehr mit den neuesten
                            Technologien kompatibel ist. Wir empfehlen eine moderne Alternative wie Firefox oder Google
                            Chrome.
                        </li>
                        <li>
                            Ein Ad Blocker kann das Laden einiger Elemente der Website verhindern. Sie können Ihren Ad
                            Blocker für die Abstimmung kurz deaktivieren oder vollständig für unsere Website
                            deaktivieren. Dann sollte die Abstimmung problemlos funktionieren.
                        </li>
                    </ul>
                </Col>
            </Row>
        </React.Fragment>
    );
};

export default SMSRequest;
